import * as React from 'react';
import { root as rootCls, dot as dotCls } from './TypingIndicator.css';
import { visuallyHidden } from '../../../mixins.css';

export interface TypingIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Accessible label announced to assistive tech (carried by a
   * visually-hidden text node inside the live region). Defaults to
   * "Workstream Agent is typing".
   */
  label?: string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * TypingIndicator — the classic "…is typing" chat affordance: a grey
 * lozenge holding three animated dots, shown below the last message while
 * the chat awaits an agent reply (see `ChatPanel`'s `awaitingReply` prop).
 *
 * A11y: the lozenge is a polite live region (`role="status"` +
 * `aria-live="polite"`) carrying a visually-hidden `label` so screen
 * readers announce e.g. "Workstream Agent is typing" when it appears; the
 * three dots are decorative (`aria-hidden`). The dot animation honours
 * `prefers-reduced-motion` (translateY wave → opacity-only pulse) in the
 * `.css.ts` sibling.
 */
export function TypingIndicator({
  label = 'Workstream Agent is typing',
  ref,
  className,
  ...rest
}: TypingIndicatorProps) {
  const cls = [rootCls, className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} role="status" aria-live="polite" {...rest}>
      <span className={visuallyHidden}>{label}</span>
      <span className={dotCls[1]} aria-hidden="true" />
      <span className={dotCls[2]} aria-hidden="true" />
      <span className={dotCls[3]} aria-hidden="true" />
    </div>
  );
}
TypingIndicator.displayName = 'TypingIndicator';
